import {
  Hexagon,
  Square,
  MousePointer2,
  Move,
  Trash2,
  Undo2,
  Redo2,
  Crosshair,
  Locate,
} from "lucide-react";
import { useStore, type DrawMode } from "../state/store";
import { BLOCK_TYPE_DEFAULTS, type BlockType } from "../types/scenario";
import { Button } from "./ui/Button";
import { BlockProperties } from "./BlockProperties";
import { getMapInstance } from "./Map2D";
import { polygonCentroid } from "../utils/geo";

interface ToolButton {
  mode: DrawMode;
  icon: typeof Hexagon;
  label: string;
  description: string;
}

const TOOLS: ToolButton[] = [
  {
    mode: "idle",
    icon: MousePointer2,
    label: "Selecteer",
    description: "Klik op een blok om het te selecteren.",
  },
  {
    mode: "draw-polygon",
    icon: Hexagon,
    label: "Polygoon",
    description: "Klik per hoekpunt; dubbelklik of Enter sluit het blok.",
  },
  {
    mode: "draw-rectangle",
    icon: Square,
    label: "Rechthoek",
    description: "Klik om de twee tegenoverliggende hoeken te bepalen.",
  },
  {
    mode: "move-vertex",
    icon: Crosshair,
    label: "Punt verplaatsen",
    description: "Sleep een hoekpunt van het geselecteerde blok.",
  },
  {
    mode: "move-block",
    icon: Move,
    label: "Blok verslepen",
    description: "Sleep het hele geselecteerde blok.",
  },
  {
    mode: "delete",
    icon: Trash2,
    label: "Verwijderen",
    description: "Klik op een blok om het te verwijderen.",
  },
];

export function ScenarioPanel() {
  const drawMode = useStore((s) => s.drawMode);
  const setDrawMode = useStore((s) => s.setDrawMode);
  const undo = useStore((s) => s.undo);
  const redo = useStore((s) => s.redo);
  const canUndo = useStore((s) => s.undoStack.length > 0);
  const canRedo = useStore((s) => s.redoStack.length > 0);
  const newBlockType = useStore((s) => s.newBlockType);
  const setNewBlockType = useStore((s) => s.setNewBlockType);
  const blocks = useStore((s) => s.scenario.blocks);
  const selectedBlockId = useStore((s) => s.selectedBlockId);
  const selectBlock = useStore((s) => s.selectBlock);

  const tool = TOOLS.find((t) => t.mode === drawMode);

  return (
    <aside className="w-64 border-r border-border bg-card flex flex-col overflow-y-auto">
      <section className="p-3 border-b border-border">
        <h2 className="text-xs font-semibold uppercase text-muted-foreground mb-2">
          Tekenen
        </h2>
        <div className="grid grid-cols-3 gap-1">
          {TOOLS.map((t) => (
            <Button
              key={t.mode}
              variant={drawMode === t.mode ? "active" : "outline"}
              size="icon"
              title={t.label}
              onClick={() => setDrawMode(t.mode)}
            >
              <t.icon size={16} />
            </Button>
          ))}
          <Button
            variant="outline"
            size="icon"
            title="Ongedaan maken"
            disabled={!canUndo}
            onClick={undo}
          >
            <Undo2 size={16} />
          </Button>
          <Button
            variant="outline"
            size="icon"
            title="Opnieuw"
            disabled={!canRedo}
            onClick={redo}
          >
            <Redo2 size={16} />
          </Button>
        </div>
        {tool && (
          <p className="text-xs text-muted-foreground mt-2">{tool.description}</p>
        )}
      </section>

      <section className="p-3 border-b border-border">
        <h2 className="text-xs font-semibold uppercase text-muted-foreground mb-2">
          Type voor nieuw blok
        </h2>
        <select
          value={newBlockType}
          onChange={(e) => setNewBlockType(e.target.value as BlockType)}
          className="w-full bg-background border border-input rounded-md px-2 py-1 text-sm"
        >
          {Object.entries(BLOCK_TYPE_DEFAULTS).map(([key, def]) => (
            <option key={key} value={key}>
              {def.label}
            </option>
          ))}
        </select>
      </section>

      <section>
        <h2 className="text-xs font-semibold uppercase text-muted-foreground p-3 pb-1">
          Blokken ({blocks.length})
        </h2>
        {blocks.length === 0 && (
          <p className="text-sm text-muted-foreground px-3 pb-3">
            Nog geen blokken. Kies een teken-tool en klik op de kaart.
          </p>
        )}
        <ul className="px-2 pb-2">
          {blocks.map((b) => {
            const def = BLOCK_TYPE_DEFAULTS[b.type];
            const isSelected = b.id === selectedBlockId;
            return (
              <li
                key={b.id}
                className={
                  "flex items-center gap-1 px-1 py-0.5 rounded-md transition-colors " +
                  (isSelected ? "bg-blue-100 text-blue-900" : "hover:bg-accent")
                }
              >
                <button
                  onClick={() => selectBlock(b.id)}
                  className="flex-1 flex items-center gap-2 px-1.5 py-1 text-left text-sm min-w-0"
                >
                  <span
                    className="w-3 h-3 rounded-sm border border-black/20 flex-none"
                    style={{ backgroundColor: b.color }}
                  />
                  <span className="flex-1 truncate">{b.name}</span>
                  <span className="text-xs text-muted-foreground flex-none">
                    {def.label}
                    {b.heightTop > 0 ? ` · ${b.heightTop}m` : ""}
                  </span>
                </button>
                <button
                  onClick={() => {
                    const map = getMapInstance();
                    if (!map) return;
                    const [lng, lat] = polygonCentroid(b.polygon);
                    map.flyTo({ center: [lng, lat], zoom: Math.max(map.getZoom(), 18) });
                    selectBlock(b.id);
                  }}
                  title="Centreer in beeld"
                  className="p-1 rounded hover:bg-blue-200/50 text-muted-foreground hover:text-foreground"
                >
                  <Locate size={14} />
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      <BlockProperties />
    </aside>
  );
}
